import { Check, Plus } from 'lucide-react';

import { _t } from '@/i18n/t';
import { cn } from '@/shared/lib/utils';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/shared/components/ui/dropdown-menu';
import type { CompanyOption } from '@/shared/utils/activeCompany';

type SidebarCompanyMenuProps = {
  companies: CompanyOption[];
  activeCompanyId: number | null;
  onSelectCompany: (id: number) => void;
  onAddCompany: () => void;
};

const SidebarCompanyMenu = ({
  companies,
  activeCompanyId,
  onSelectCompany,
  onAddCompany,
}: SidebarCompanyMenuProps) => (
  <DropdownMenuContent
    side="bottom"
    align="end"
    sideOffset={10}
    alignOffset={-4}
    className="th w-[14rem] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-hidden rounded-md border border-nav-border bg-card px-0 py-1"
  >
    {companies.length > 0 && (
      <DropdownMenuLabel className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-nav-heading">
        {_t('sidebar.company.sectionLabel')}
      </DropdownMenuLabel>
    )}

    <div className="max-h-[11.875rem] overflow-y-auto scrollbar-subtle">
      {companies.map((company) => (
        <DropdownMenuItem
          key={company.value}
          onSelect={() => onSelectCompany(company.value)}
          title={company.label}
          className="gap-2 rounded-none px-3 py-2 text-nav-textStrong"
        >
          <span
            className={cn(
              'flex-1 truncate',
              company.value === activeCompanyId && 'font-semibold',
            )}
          >
            {company.label}
          </span>
          {company.value === activeCompanyId && (
            <Check data-testid="sidebar-company-menu-active" className="size-4 flex-shrink-0 text-primary" />
          )}
        </DropdownMenuItem>
      ))}
    </div>

    {companies.length > 0 && <DropdownMenuSeparator className="mx-0" />}

    <DropdownMenuItem onSelect={onAddCompany} className="gap-2 rounded-none px-3 py-2 text-nav-textStrong">
      <Plus className="size-4 flex-shrink-0" />
      {_t('sidebar.company.add')}
    </DropdownMenuItem>
  </DropdownMenuContent>
);

export default SidebarCompanyMenu;
